import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Breadcrumb from '@/components/Breadcrumb';
import BrandBannerSection from '@/sections/brand/BrandBannerSection';
import BrandCardsSection from '@/sections/brand/BrandCardsSection';
import { BrandBannerSkeleton, BrandCardsSkeleton } from '@/components/Skeleton';
import useMinimumLoadingTime from '@/hooks/useMinimumLoadingTime';
import { getBrands, getCards } from '@/api/products';

const BRAND_FILTER_STATE_KEY = 'brand_page_filters';

const getStoredBrandFilters = (brandId) => {
  if (!brandId) {
    return {
      category: 'ALL',
      sort: { field: 'POPULARITY', direction: 'DESC' },
    };
  }

  try {
    const cached = sessionStorage.getItem(`${BRAND_FILTER_STATE_KEY}_${brandId}`);
    if (cached) {
      const parsed = JSON.parse(cached);
      return {
        category: parsed.category || 'ALL',
        sort:
          parsed.sort?.field && parsed.sort?.direction
            ? parsed.sort
            : { field: 'POPULARITY', direction: 'DESC' },
      };
    }
  } catch (error) {
    console.warn('Failed to parse brand filter cache', error);
    sessionStorage.removeItem(`${BRAND_FILTER_STATE_KEY}_${brandId}`);
  }

  return {
    category: 'ALL',
    sort: { field: 'POPULARITY', direction: 'DESC' },
  };
};

const clearBrandFilterState = (brandId) => {
  if (!brandId) return;
  try {
    sessionStorage.removeItem(`${BRAND_FILTER_STATE_KEY}_${brandId}`);
  } catch (error) {
    console.warn('Failed to clear brand filter cache', error);
  }
};

const BrandPage = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const initialFilters = useRef(getStoredBrandFilters(id));
  // Clear cached filters on full reload
  useEffect(() => {
    const navigationEntries = performance?.getEntriesByType?.('navigation');
    const isReloadNavigation =
      navigationEntries?.[0]?.type === 'reload' || performance?.navigation?.type === 1;

    if (isReloadNavigation) {
      sessionStorage.removeItem(`${BRAND_FILTER_STATE_KEY}_${id}`);
    }
  }, [id]);

  const [selectedCategory, setSelectedCategory] = useState(initialFilters.current.category);
  const [sortConfig, setSortConfig] = useState(initialFilters.current.sort);

  // Fetch brands
  const { data: brandsData, isLoading: isLoadingBrands } = useQuery({
    queryKey: ['brands'],
    queryFn: getBrands,
    select: data => data.data
  });

  // Find current brand information from the list
  const brandInfo = brandsData?.find(brand => brand.id === id);

  // Fetch cards for this brand with infinite query
  const { 
    data: cardsData, 
    isLoading: isLoadingCards,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage
  } = useInfiniteQuery({
    queryKey: ['brand-cards', id, selectedCategory, sortConfig],
    queryFn: ({ pageParam = null }) => getCards({
      ...(selectedCategory !== 'ALL' && { type: selectedCategory }),
      sort: sortConfig.field,
      direction: sortConfig.direction,
      limit: 40,
      brandId: id,
      cursor: pageParam
    }),
    getNextPageParam: (lastPage) => lastPage.cursor,
    initialPageParam: null,
    enabled: !!id,
  });


  // Category options
  const categoryOptions = [
    { value: 'ALL', label: t('home.filters.categories.all') },
    { value: 'SINGLE_CARD', label: t('home.filters.categories.single') },
    { value: 'CARD_BOX', label: t('home.filters.categories.box') },
  ];

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
  };

  const handleSortChange = (sortItem, direction) => {
    // Map the sort values to API expected values
    const sortMapping = {
      'date': 'RELEASE_DATE',
      'price': 'LOWEST_PRICE',
      'popularity': 'POPULARITY'
    };
    
    setSortConfig({ 
      field: sortMapping[sortItem.value], 
      direction: direction.toUpperCase() 
    });
  };

  // Persist filters per brand
  useEffect(() => {
    if (!id) return;

    const filtersToCache = {
      category: selectedCategory,
      sort: sortConfig,
    };

    try {
      sessionStorage.setItem(`${BRAND_FILTER_STATE_KEY}_${id}`, JSON.stringify(filtersToCache));
    } catch (error) {
      console.warn('Failed to persist brand filters', error);
    }
  }, [id, selectedCategory, sortConfig]);

  useEffect(() => {
    if (!id) return;

    const handleBeforeUnload = () => {
      clearBrandFilterState(id);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [id]);

  const handleLoadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  // Track pages loaded for auto load more
  const allCards = cardsData?.pages?.flatMap(page => page.data) || [];
  const pagesLoaded = cardsData?.pages?.length || 0;

  // Handle infinite scroll
  const handleScroll = useCallback(() => {
    if (isFetchingNextPage || !hasNextPage || pagesLoaded >= 1) return;
    
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const windowHeight = window.innerHeight;
    const docHeight = document.documentElement.scrollHeight;
    
    // Trigger load more when user scrolls to within 200px of the bottom
    if (scrollTop + windowHeight >= docHeight - 200) {
      handleLoadMore();
    }
  }, [isFetchingNextPage, hasNextPage, handleLoadMore, pagesLoaded]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Check scroll position after loading completes
  useEffect(() => {
    if (!isFetchingNextPage && hasNextPage && allCards.length > 0 && pagesLoaded < 3) {
      // Small delay to ensure DOM is updated
      const timer = setTimeout(() => {
        handleScroll();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isFetchingNextPage, hasNextPage, allCards.length, pagesLoaded, handleScroll]);

  // Apply minimum loading time to loading states
  const showBrandsLoading = useMinimumLoadingTime(isLoadingBrands);
  const showCardsLoading = useMinimumLoadingTime(isLoadingCards && allCards.length === 0);

  const breadcrumbItems = [
    { label: t('common.home'), href: '/' },
    { label: brandInfo?.localizedName },
  ];

  if (showBrandsLoading) {
    return (
      <div className="container pb-8 mt-6">
        <Breadcrumb items={[
          { label: t('common.home'), href: '/' },
          { label: '' },
        ]} />

        <div className='h-6' />

        <BrandBannerSkeleton />

        <div className='h-8' />
        
        <BrandCardsSkeleton />
      </div>
    );
  }

  return (
    <div className="container pb-8 mt-6">
      <Breadcrumb items={breadcrumbItems} />

      <div className='h-6' />

      <BrandBannerSection brandInfo={brandInfo} />

      <div className='h-8' />
      
      <BrandCardsSection
        cards={allCards}
        categories={categoryOptions}
        selectedCategory={selectedCategory}
        onCategoryChange={handleCategoryChange}
        onSortChange={handleSortChange}
        onLoadMore={handleLoadMore}
        hasMoreCards={hasNextPage}
        isLoading={showCardsLoading}
        isLoadingMore={isFetchingNextPage}
        brandInfo={brandInfo}
        pagesLoaded={pagesLoaded}
      />
    </div>
  );
};

export default BrandPage; 